import React, { useCallback } from 'react';
import {
  Link,
  AppContent,
  Button,
  ButtonGroup,
  Icon,
  Tabs,
} from '@common/components';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { useNavigate, useParams } from '@common/hooks';
import { ShiftOpeningsList, ShiftsCalendar } from '@scheduling/components';
import { Dropdown, Row, Typography } from 'antd';
import { ShiftMessages } from '@scheduling/components/shift-messages';
import { useCurrentMembership, useCurrentEntity } from '@identity/hooks';
import { useFeatureFlags } from '@common/contexts/feature-flags';
import { buildDialogUrl } from '@common/services';
import { LineHeight } from '@common/tokens';

const { Text } = Typography;

export function ShiftPage() {
  const navigate = useNavigate();
  const { type = 'feed' } = useParams<{ type: string }>();
  setBreadcrumbs([{ name: 'Shifts', path: '/works/shifts' }]);

  const currentMembership = useCurrentMembership();
  const currentEntity = useCurrentEntity();
  const onDemandEnabled = currentEntity.onDemandEnabled;

  const handleTabChange = useCallback(
    (key: React.Key) => {
      navigate(`/works/shifts/${key}`);
    },
    [navigate]
  );

  const canCreate = currentMembership.canCreateShiftGaps;
  const canUploadShiftGaps = currentMembership.canUploadShiftGaps;
  const { isBulkShiftIncentiveOverridesEnabled } = useFeatureFlags();
  const showBulkShiftOverrideEntry =
    isBulkShiftIncentiveOverridesEnabled &&
    currentMembership.shiftRateEditsPermitted;
  const createShiftDrawerUrl = buildDialogUrl('create-shift-drawer');

  const dropDownItems = [
    canCreate && {
      key: 'add',
      label: (
        <Link to={createShiftDrawerUrl}>
          <Icon icon="plus" />
          <Text> Create Shift</Text>
        </Link>
      ),
    },
    canUploadShiftGaps && {
      key: 'add-multiple',
      label: (
        <Link to="/works/shifts/feed/add-multiple">
          <Icon kind="regular" icon="arrowUpFromBracket" />
          <Text> Upload Shifts</Text>
        </Link>
      ),
    },
    showBulkShiftOverrideEntry && {
      key: 'bulk-override-rates',
      label: (
        <Link to="/works/shifts/feed/bulk-override-rates">
          <Icon kind="light" icon="dollarSign" />
          <Text> Bulk Override Rates</Text>
        </Link>
      ),
    },
  ].filter(Boolean) as { key: string; label: React.ReactNode }[];

  const ActionButtons = () => {
    const shiftTabs = type === 'feed' || type === 'calendar';
    const showAdd = dropDownItems.length > 0 && shiftTabs;
    return (
      <>
        <Row className="action-buttons">
          <ButtonGroup>
            {type === 'feed' && (
              <span id="wk-portal-target:org-shift-list-extra-actions" />
            )}
            {showAdd && (
              <Dropdown
                menu={{ items: dropDownItems }}
                className="shift-dropdown"
              >
                <Button
                  size="md"
                  icon={<Icon icon="calendar" kind="light" />}
                  iconRight={
                    <Icon
                      icon="chevronDown"
                      size="sm"
                      kind="regular"
                      color="steelDarker"
                    />
                  }
                  onClick={(e) => e.preventDefault()}
                >
                  Shifts
                </Button>
              </Dropdown>
            )}
          </ButtonGroup>
        </Row>
        <style jsx>{`
          :global(.ShiftTabs .ant-tabs-extra-content) {
            line-height: ${LineHeight.xxl};
            align-self: flex-start;
          }
          :global(.shift-dropdown:hover) {
            opacity: 90%;
          }
          :global(.action-buttons) {
            gap: 8px;
          }
        `}</style>
      </>
    );
  };

  return (
    <AppContent>
      {!!onDemandEnabled && (
        <>
          <Tabs
            className="ShiftTabs"
            onChange={handleTabChange}
            activeKey={type}
            tabBarExtraContent={<ActionButtons />}
            items={[
              {
                label: 'Calendar',
                key: 'calendar',
                children: <ShiftsCalendar />,
              },
              {
                label: 'List',
                key: 'feed',
                children: <ShiftOpeningsList triggerPortal={type} />,
              },
              {
                label: 'Messages',
                key: 'messages',
                children: <ShiftMessages />,
              },
            ]}
          ></Tabs>
        </>
      )}
    </AppContent>
  );
}
